/**
 * El blog: lo que comparten el worker, el panel y el editor.
 *
 * Un blog por sitio, y sus entradas viven en la base —no en el proyecto del
 * editor—, igual que las ofertas. El motivo es la publicación: las fichas del
 * CMS son HTML que el editor genera al publicar el sitio, así que una entrada
 * escrita en el panel no saldría hasta republicarlo entero. Aquí una entrada
 * sale al guardarla.
 *
 * Todo lo que decide qué es válido está aquí y no repetido en cada lado: el
 * panel avisa con las mismas reglas con las que el worker rechaza.
 */
export const BLOG_LIMITS = {
    title: 200,
    slug: 120,
    excerpt: 500,
    /** Un artículo largo, con sus imágenes enlazadas, cabe de sobra. */
    content: 200_000,
    author: 120,
    authors: 10,
    categories: 10,
    category: 40,
    url: 2_000,
    seoTitle: 200,
    seoDescription: 500,
    name: 120,
};
/** La firma conserva su campo de texto compatible con las entradas anteriores.
 * Cada nombre se elige por separado; la coma separa los coautores al guardarlos. */
export const BLOG_AUTHORS_MAX_LENGTH = BLOG_LIMITS.author * BLOG_LIMITS.authors + (BLOG_LIMITS.authors - 1) * 2;
export function blogAuthorNames(value) {
    const names = [];
    for (const part of value.split(',')) {
        const name = part.trim();
        if (name && !names.some((existing) => existing.localeCompare(name, undefined, { sensitivity: 'accent' }) === 0))
            names.push(name);
    }
    return names;
}
export function blogAuthorValue(names) {
    return blogAuthorNames(names.map((name) => blogPlainText(name, BLOG_LIMITS.author)).join(', '))
        .slice(0, BLOG_LIMITS.authors).join(', ');
}
export function blogPostState(post, now = new Date()) {
    if (post.status !== 'published')
        return 'draft';
    const at = post.publishedAt ? new Date(post.publishedAt).getTime() : 0;
    return at > now.getTime() ? 'scheduled' : 'published';
}
/**
 * La dirección de una entrada a partir de su título: «¿Cómo elegir café?» →
 * «como-elegir-cafe». Sin acentos ni signos, para que se pueda escribir y
 * compartir tal cual. Un título sin nada aprovechable da «entrada».
 */
export function blogSlug(value) {
    const slug = value
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, BLOG_LIMITS.slug)
        .replace(/-+$/g, '');
    return slug || 'entrada';
}
export const BLOG_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
/** Minutos de lectura, a 200 palabras por minuto. Nunca menos de uno. */
export function blogReadingMinutes(html) {
    const words = html.replace(/<[^>]*>/g, ' ').split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.round(words / 200));
}
export const DEFAULT_BLOG_SETTINGS = { enabled: true, name: '', basePath: 'blog' };
/**
 * Rutas que ya sirve el worker en el dominio del sitio: el blog no puede vivir
 * en ninguna, o taparía el catálogo, el pago o los archivos.
 */
export const BLOG_RESERVED_PATHS = new Set(['bookings', 'digital', 'services', 'checkout', '_veltica', 'assets', 'api', 'account']);
export function blogBasePathValid(value) {
    return value.length > 0 && value.length <= 60 && BLOG_SLUG_PATTERN.test(value) && !BLOG_RESERVED_PATHS.has(value);
}
// ─── El cuerpo de una entrada ────────────────────────────────────────────────
/* `h1` también: se convertía en `h2` para dejar un solo `h1` por página —el
   título de la entrada—, y quien elegía «Título 1» en el editor lo veía volver
   a «Título 2» al guardar. Lo que se escribe se respeta; HTML5 admite varios. */
const ALLOWED_TAGS = new Set([
    'p', 'br', 'h1', 'h2', 'h3', 'h4', 'strong', 'em', 'u', 's', 'a', 'ul', 'ol', 'li',
    'blockquote', 'code', 'pre', 'hr', 'img', 'figure', 'figcaption',
    // Sólo con las clases de los bloques del editor (ver `BLOCK_CLASSES`).
    'div', 'span', 'iframe',
]);
/**
 * Los bloques del editor de fichas (`CmsBlockEditor`) que no existen en HTML
 * llano: vídeo, destacado y botón. Son marcado corriente con una clase, y su
 * aspecto vive en `richContentCss`. Un `div` o `span` sin una de éstas se
 * quita, conservando lo que tenga dentro.
 */
const BLOCK_CLASSES = {
    div: new Set(['cms-embed', 'cms-callout', 'cms-callout-body']),
    span: new Set(['cms-callout-mark']),
    p: new Set(['cms-cta']),
    figure: new Set(['cms-figure']),
};
const FIGURE_SIZES = new Set(['small', 'medium', 'large', 'full', 'custom']);
const FIGURE_ALIGNS = new Set(['left', 'center', 'right']);
/** Los únicos reproductores: los que genera el editor, con su id y nada más. */
const EMBED_SOURCES = [
    {
        pattern: /^https:\/\/www\.youtube(?:-nocookie)?\.com\/embed\/([\w-]{11})$/,
        src: (id) => `https://www.youtube.com/embed/${id}`,
        title: 'YouTube',
        allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
    },
    {
        pattern: /^https:\/\/player\.vimeo\.com\/video\/(\d{6,})$/,
        src: (id) => `https://player.vimeo.com/video/${id}`,
        title: 'Vimeo',
        allow: 'autoplay; fullscreen; picture-in-picture',
    },
];
const VOID_TAGS = new Set(['br', 'hr', 'img']);
/** Nombres de siempre que significan lo mismo que uno permitido. */
const TAG_ALIASES = { b: 'strong', i: 'em', strike: 's', del: 's', h5: 'h4', h6: 'h4' };
/** Lo que se va CON su contenido: su texto no es texto del artículo. */
const DROPPED_WITH_CONTENT = ['script', 'style', 'object', 'embed', 'noscript', 'template', 'svg', 'math', 'textarea', 'select', 'title', 'head'];
const escapeAttr = (value) => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const escapeTextKeepingEntities = (value) => value
    .replace(/&(?!(?:[a-zA-Z][a-zA-Z0-9]*|#\d+|#x[0-9a-fA-F]+);)/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
/** Lo que el navegador leería en un atributo, para juzgar su esquema. */
function decodeForCheck(value) {
    return value
        .replace(/&#x([0-9a-f]+);?/gi, (_, hex) => String.fromCodePoint(parseInt(hex, 16)))
        .replace(/&#(\d+);?/g, (_, dec) => String.fromCodePoint(Number(dec)))
        .replace(/&colon;/gi, ':')
        .replace(/&tab;|&newline;/gi, '')
        .replace(/[\u0000- \u007f-\u009f]+/g, '')
        .toLowerCase();
}
/** Enlaces: web, correo, teléfono, anclas y rutas del propio sitio. */
function safeHref(value) {
    const check = decodeForCheck(value);
    if (!check)
        return null;
    if (/^(https?:|mailto:|tel:)/.test(check) || check.startsWith('#') || (check.startsWith('/') && !check.startsWith('//'))) {
        return value.trim().slice(0, BLOG_LIMITS.url);
    }
    // Una dirección escrita sin esquema («ejemplo.com/x»), no un esquema raro.
    if (!/^[a-z][a-z0-9+.-]*:/.test(check) && !check.startsWith('//'))
        return value.trim().slice(0, BLOG_LIMITS.url);
    return null;
}
/**
 * Una imagen suelta —la portada, la de compartir—: la dirección si es de la
 * web o del sitio, y nada si no. Las mismas reglas que las del cuerpo.
 */
export function blogImageUrl(value) {
    return value.trim() ? safeSrc(value) ?? '' : '';
}
/** Imágenes: sólo de la web o del propio sitio. Nada de `data:`. */
function safeSrc(value) {
    const check = decodeForCheck(value);
    if (/^https?:\/\//.test(check) || (check.startsWith('/') && !check.startsWith('//')))
        return value.trim().slice(0, BLOG_LIMITS.url);
    return null;
}
function parseAttributes(raw) {
    const attrs = new Map();
    const pattern = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+))/g;
    let match;
    while ((match = pattern.exec(raw))) {
        const name = match[1].toLowerCase();
        if (!attrs.has(name))
            attrs.set(name, match[2] ?? match[3] ?? match[4] ?? '');
    }
    return attrs;
}
/** La clase conocida de un bloque, si la trae. */
function blockClass(tag, attrs) {
    const known = BLOCK_CLASSES[tag];
    if (!known)
        return null;
    return (attrs.get('class') ?? '').split(/\s+/).find((name) => known.has(name)) ?? null;
}
/**
 * La etiqueta reconstruida, o `null` si se quita. Para un `div`/`span` sin
 * clase conocida también `null`: se quita la etiqueta pero no lo de dentro.
 */
/**
 * La alineación de un bloque de texto, lo ÚNICO que se lee de su `style`: el
 * editor la pone así (izquierda, centro, derecha, justificado) y se reescribe
 * con el valor ya comprobado, nunca copiando el atributo.
 */
const TEXT_ALIGNS = new Set(['left', 'center', 'right', 'justify']);
const ALIGNABLE_TAGS = new Set(['p', 'h1', 'h2', 'h3', 'h4', 'blockquote', 'li']);
function alignAttr(tag, attrs) {
    if (!ALIGNABLE_TAGS.has(tag))
        return '';
    const value = /text-align\s*:\s*([a-z]+)/i.exec(attrs.get('style') ?? '')?.[1]?.toLowerCase()
        ?? (attrs.get('align') ?? '').toLowerCase();
    return value && TEXT_ALIGNS.has(value) && value !== 'left' ? ` style="text-align:${value}"` : '';
}
function openTag(tag, rawAttrs) {
    const attrs = parseAttributes(rawAttrs);
    if (tag === 'div' || tag === 'span') {
        const cls = blockClass(tag, attrs);
        return cls ? `<${tag} class="${cls}">` : null;
    }
    if (tag === 'p') {
        const cls = blockClass(tag, attrs);
        return cls ? `<p class="${cls}"${alignAttr(tag, attrs)}>` : `<p${alignAttr(tag, attrs)}>`;
    }
    if (tag === 'figure') {
        if (!blockClass(tag, attrs))
            return '<figure>';
        const size = attrs.get('data-size') ?? '';
        const align = attrs.get('data-align') ?? '';
        const width = /max-width\s*:\s*(\d{1,3})%/i.exec(attrs.get('style') ?? '')?.[1];
        const percent = width ? Math.max(15, Math.min(100, Number(width))) : null;
        return '<figure class="cms-figure"'
            + (FIGURE_SIZES.has(size) ? ` data-size="${size}"` : '')
            + (FIGURE_ALIGNS.has(align) ? ` data-align="${align}"` : '')
            + (size === 'custom' && percent ? ` style="max-width:${percent}%"` : '')
            + '>';
    }
    if (tag === 'iframe') {
        const src = (attrs.get('src') ?? '').trim();
        for (const source of EMBED_SOURCES) {
            const id = source.pattern.exec(src)?.[1];
            if (id)
                return `<iframe src="${source.src(id)}" title="${source.title}" frameborder="0" allow="${source.allow}" allowfullscreen loading="lazy">`;
        }
        return null;
    }
    if (tag === 'a') {
        const href = safeHref(attrs.get('href') ?? '');
        if (!href)
            return '<a>';
        const external = /^https?:/i.test(decodeForCheck(href));
        const newTab = attrs.get('target') === '_blank';
        return `<a href="${escapeAttr(href)}"${newTab ? ' target="_blank"' : ''}${external || newTab ? ' rel="noopener noreferrer"' : ''}>`;
    }
    if (tag === 'img') {
        const src = safeSrc(attrs.get('src') ?? '');
        if (!src)
            return null;
        const alt = (attrs.get('alt') ?? '').slice(0, 300);
        return `<img src="${escapeAttr(src)}" alt="${escapeAttr(alt)}" loading="lazy">`;
    }
    return `<${tag}${alignAttr(tag, attrs)}>`;
}
/**
 * El HTML de una entrada, limpio.
 *
 * Por lista permitida y RECONSTRUYENDO cada etiqueta, no filtrando la que
 * llega: de la entrada sólo se aprovechan el nombre de la etiqueta y los pocos
 * atributos que se conocen, y lo que sale lo escribe esta función. Así no hay
 * atributo raro, manejador `on…` ni esquema `javascript:` que se cuele por un
 * hueco del filtro, porque no se copia nada que no se haya leído a propósito.
 *
 * El texto se vuelve a escapar, las etiquetas se cierran en orden y lo que
 * quede abierto se cierra al final: la salida siempre es HTML bien formado,
 * que el worker puede meter en cualquier plantilla sin romperla.
 */
export function sanitizeBlogHtml(input) {
    let html = String(input ?? '').slice(0, BLOG_LIMITS.content * 2);
    html = html.replace(/<!--[\s\S]*?(?:-->|$)/g, '');
    // Lo de dentro de un reproductor es texto de respaldo, nunca del artículo.
    html = html.replace(/(<iframe\b[^>]*>)[\s\S]*?(<\/iframe\s*>)/gi, '$1$2');
    for (const tag of DROPPED_WITH_CONTENT) {
        html = html.replace(new RegExp(`<${tag}\\b[\\s\\S]*?<\\/${tag}\\s*>`, 'gi'), '');
        // Abierta y nunca cerrada: el resto del documento sería su contenido.
        const open = html.search(new RegExp(`<${tag}\\b`, 'i'));
        if (open >= 0)
            html = html.slice(0, open);
    }
    const out = [];
    /* Cada apertura, escrita o no. Las que se quitaron también se apuntan: así su
       cierre las encuentra a ellas y no cierra por error un bloque de fuera con
       el mismo nombre. */
    const stack = [];
    const closeTo = (at) => {
        while (stack.length > at) {
            const open = stack.pop();
            if (open.emitted)
                out.push(`</${open.tag}>`);
        }
    };
    for (const piece of html.split(/(<[^<>]*>)/)) {
        if (!piece)
            continue;
        const tagMatch = /^<\s*(\/)?\s*([a-zA-Z][a-zA-Z0-9]*)([^>]*)>$/.exec(piece);
        if (!tagMatch) {
            // Texto, o un «<» suelto que no abre nada: se escapa entero.
            out.push(escapeTextKeepingEntities(piece));
            continue;
        }
        const closing = Boolean(tagMatch[1]);
        const name = tagMatch[2].toLowerCase();
        const tag = TAG_ALIASES[name] ?? name;
        if (!ALLOWED_TAGS.has(tag))
            continue;
        if (closing) {
            if (VOID_TAGS.has(tag))
                continue;
            const at = stack.map((open) => open.tag).lastIndexOf(tag);
            if (at >= 0)
                closeTo(at);
            continue;
        }
        const opened = openTag(tag, tagMatch[3] ?? '');
        const selfClosed = VOID_TAGS.has(tag) || /\/\s*$/.test(tagMatch[3] ?? '');
        if (opened)
            out.push(opened);
        // Un reproductor que no es de los conocidos: fuera, y lo de dentro también.
        if (!opened && tag === 'iframe')
            continue;
        if (!selfClosed)
            stack.push({ tag, emitted: Boolean(opened) });
    }
    closeTo(0);
    // Sin recortar aquí: partiría una etiqueta. El largo se valida al recibirlo.
    return out.join('');
}
/** Texto plano de una línea: sin etiquetas, sin saltos, recortado. */
export function blogPlainText(value, max) {
    return String(value ?? '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, max)
        .trimEnd();
}
// ─── La plantilla de la entrada, diseñada en el editor ───────────────────────
/**
 * Dónde publica el editor la plantilla de la entrada (ver
 * `buildPublishedSiteFiles`): UNA página para todas, con un marcador en cada
 * campo enlazado. El worker la rellena con la entrada que se pide
 * (`fillBlogTemplate`); sin ella, pinta su diseño por defecto.
 */
export const BLOG_TEMPLATE_PATH = '_blog/post.html';
/**
 * Las páginas del sitio que van al `sitemap.xml`, como las dejó la última
 * publicación: `{ indexable, paths }`. Lo escribe el editor y lo lee el worker,
 * que le suma el blog y las ofertas. Como `_blog/`, nunca se sirve tal cual.
 */
export const SITEMAP_PAGES_PATH = '_sitemap/pages.json';
/**
 * El índice del buscador del sitio, como lo dejó la última publicación: una
 * lista de `{ t, u, d, x }` —título, dirección, descripción y texto— por
 * página. El worker lo sirve en `/_search.json` sumándole las entradas del blog.
 */
export const SEARCH_PAGES_PATH = '_search/pages.json';
/** Los campos que se enlazan en la plantilla, con el marcador que los ocupa al publicar. */
export const BLOG_TEMPLATE_FIELDS = ['title', 'excerpt', 'image', 'content', 'author', 'date', 'categories', 'seoTitle', 'seoDescription', 'seoImage', 'readingTime', 'authorAvatar'];
/**
 * El marcador de un campo. Letras, dos puntos y porcentajes: sobreviven a todo
 * lo que el publicador hace con un texto —escaparlo, meterlo en un atributo,
 * renombrar prefijos— sin cambiar un carácter.
 */
export function blogTemplateMarker(field) {
    return `%%BLOG:${field}%%`;
}
/**
 * El slug en la plantilla. Sólo letras: los enlaces de una tarjeta se arman
 * con `slugifyPathSegment`, que se comería los porcentajes de los demás.
 */
export const BLOG_SLUG_MARKER = 'zzblogslugzz';
/** La plantilla de la LISTA del blog (`/blog`), diseñada en el editor. */
export const BLOG_INDEX_TEMPLATE_PATH = '_blog/index.html';
/**
 * Lo que encierra UNA tarjeta de la lista en la plantilla publicada: el editor
 * la pinta una vez, con marcadores, entre `<!--BLOG:ITEM:desde:cuántas-->` y
 * `BLOG_ITEM_END`, y el worker la repite con esas entradas
 * (`fillBlogIndexTemplate`). Desde y cuántas son los de su repetidor: una lista
 * con la destacada arriba (una, desde 0) y las demás debajo (desde 1).
 */
export const BLOG_ITEM_START = '<!--BLOG:ITEM';
export const BLOG_ITEM_END = '<!--/BLOG:ITEM-->';
/**
 * Dónde va «‹ 1 2 3 ›» en la lista del blog publicada: lo deja el editor tras
 * la lista con «por página» (la de `/blog`), y el worker lo cambia por el
 * paginador de la página pedida (`?p=`), o por nada si cabe en una.
 */
export const BLOG_PAGER_MARKER = '<!--BLOG:PAGER-->';
/** Una portada usa sólo fotos y no consume el aviso de lista vacía. */
export function blogItemStart(offset, limit, cover = false) {
    return `${BLOG_ITEM_START}:${Math.max(0, offset)}:${Math.max(1, limit)}${cover ? ':COVER' : ''}-->`;
}
/** El id de la ficha inventada con la que se publican las plantillas del blog. */
export const BLOG_TEMPLATE_ITEM_ID = 'blog-template';
/**
 * La portada de una entrada que no tiene: el color de acento del sitio con el
 * icono de un artículo encima, en `ink` (el color que contrasta con el acento,
 * lo calcula quien la pinta con el tema). Lleva las clases de la imagen a la que
 * sustituye, así que mide y se redondea igual que ella.
 */
export function blogCoverPlaceholderHtml(className, ink) {
    const cls = className ? ` class="${escapeTemplateText(className)}"` : '';
    return `<span${cls} role="img" aria-hidden="true" data-blog-cover-empty style="display:flex;align-items:center;justify-content:center;background:var(--veltica-accent,var(--wb-accent));color:${escapeTemplateText(ink)}">`
        + '<svg viewBox="0 0 24 24" style="width:min(56px,28%);height:auto" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">'
        + '<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 8h10M7 12h10M7 16h6"/></svg></span>';
}
const escapeTemplateText = (value) => value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
/**
 * La plantilla con una entrada dentro.
 *
 * Cada marcador se cambia por su valor escapado —vale igual dentro de un texto
 * que de un atributo—, salvo el cuerpo, que llega ya saneado
 * (`sanitizeBlogHtml`) y va tal cual. Una imagen sin foto se quita entera: una
 * `<img>` con el marcador de dirección sería un recuadro roto.
 */
export function fillBlogTemplate(html, values, options = {}) {
    let out = html;
    /* Sin portada, en lugar de la `<img>` —que saldría rota— su hueco con el color
       de acento y el icono; sin tinta para el icono, se quita como antes. */
    if (!values.image) {
        out = out.replace(new RegExp(`<img\\b[^>]*${escapeRegExp(blogTemplateMarker('image'))}[^>]*>`, 'g'), (tag) => (options.coverInk
            /* Con la marca de foto de la tarjeta, si la traía: la lista en filas la
               busca para dejarla a la izquierda. */
            ? blogCoverPlaceholderHtml(/\bclass="([^"]*)"/.exec(tag)?.[1] ?? '', options.coverInk)
                .replace('<span', `<span${(/\sdata-(?:wb|veltica)-offer-field="image"/.exec(tag) ?? [''])[0]}`)
            : ''));
    }
    /* Un autor sin foto: sus iniciales en el mismo hueco (mismas clases, misma medida). */
    if (!values.authorAvatar) {
        out = out.replace(new RegExp(`<img\\b[^>]*${escapeRegExp(blogTemplateMarker('authorAvatar'))}[^>]*>`, 'g'), (tag) => {
            const cls = /\bclass="([^"]*)"/.exec(tag)?.[1];
            const letters = (values.author ?? '').split(',')[0].trim().split(/\s+/).filter(Boolean);
            const text = ((letters[0]?.[0] ?? '') + (letters.length > 1 ? letters[letters.length - 1][0] ?? '' : '')).toLocaleUpperCase();
            return text
                ? `<span${cls ? ` class="${escapeTemplateText(cls)}"` : ''} aria-hidden="true" style="display:grid;place-items:center;overflow:hidden;border-radius:999px;background:color-mix(in srgb,var(--veltica-primary,var(--wb-primary,currentColor)) 14%,transparent);color:var(--veltica-primary,var(--wb-primary,currentColor));font-weight:700;font-size:.8em">${escapeTemplateText(text)}</span>`
                : '';
        });
    }
    for (const field of BLOG_TEMPLATE_FIELDS) {
        const value = field === 'content' ? values.content : escapeTemplateText(values[field] ?? '');
        out = out.split(blogTemplateMarker(field)).join(value);
    }
    return out.split(BLOG_SLUG_MARKER).join(encodeURIComponent(values.slug ?? ''));
}
/**
 * La lista del blog con sus entradas: la tarjeta que hay entre `BLOG_ITEM_START`
 * y `BLOG_ITEM_END` se repite una vez por entrada, rellena. Si la plantilla no
 * trae tarjeta, sale tal cual.
 */
export function fillBlogIndexTemplate(html, posts, options = {}) {
    /* Sin ninguna entrada, en lugar de la tarjeta —que saldría vacía, con su
       «Leer más» suelto— el aviso de que aún no hay (sólo en la primera lista). */
    if (!posts.length) {
        let first = true;
        return html.replace(/<!--BLOG:ITEM(?::(\d+):(\d+))?(:COVER)?-->([\s\S]*?)<!--\/BLOG:ITEM-->/g, (_match, from, count, cover, card) => {
            if (cover)
                return (options.coverPosts ?? []).filter((post) => post.image.trim())
                    .slice(Number(from ?? 0), Number(from ?? 0) + Number(count ?? 1))
                    .map((post) => fillBlogTemplate(card, post, options)).join('');
            const out = first ? options.emptyHtml ?? '' : '';
            first = false;
            return out;
        });
    }
    /* Cada repetidor, con sus entradas: `desde` y `cuántas` vienen en su marca. */
    return html.replace(/<!--BLOG:ITEM(?::(\d+):(\d+))?(:COVER)?-->([\s\S]*?)<!--\/BLOG:ITEM-->/g, (_match, from, count, cover, card) => {
        const offset = Number(from ?? 0) || 0;
        const limit = Number(count ?? posts.length) || posts.length;
        const source = cover ? (options.coverPosts ?? posts).filter((post) => post.image.trim()) : posts;
        return source.slice(offset, offset + limit).map((post) => {
            const filled = fillBlogTemplate(card, post, options);
            return post.comingSoon ? filled.replace(/^(\s*<[a-zA-Z][\w-]*)/, '$1 data-veltica-coming-soon="1"') : filled;
        }).join('');
    });
}
function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
// ─── Los diseños de entrada de la biblioteca ─────────────────────────────────
/**
 * La categoría de los templates de página que son diseños de ENTRADA de blog.
 *
 * No es un `PAGE_TYPES`: esos los elige la IA al armar un sitio, y un diseño de
 * entrada sólo tiene sentido como plantilla del blog. Se listan en la galería
 * del editor (pestaña Blog → «Elegir diseño») y en ningún otro sitio.
 */
export const BLOG_POST_TEMPLATE_CATEGORY = 'blog-post';
/** Y los de la LISTA del blog (`/blog`): otra galería, otra página. */
export const BLOG_INDEX_TEMPLATE_CATEGORY = 'blog-index';
/**
 * Los diseños de las páginas de una colección del CMS en la biblioteca, como
 * los del blog: `cms-item` es la página de CADA ficha (`/coleccion/[slug]`) y
 * `cms-collection`, la que las lista todas. No son `PAGE_TYPES`: la IA de sitios
 * y «Nueva página» no los ofrecen; se eligen en Templates con la página de la
 * colección abierta (ver `scripts/cms-disenos.mjs`).
 */
export const CMS_ITEM_TEMPLATE_CATEGORY = 'cms-item';
export const CMS_COLLECTION_TEMPLATE_CATEGORY = 'cms-collection';
/**
 * Las marcas de un diseño de CMS y QUÉ buscan en la colección.
 *
 * A diferencia del blog, cada colección tiene sus campos: la marca no dice un
 * campo sino un papel —el título, la imagen, el resumen…— y el editor elige el
 * campo que lo cumple (`bindCmsSlots`). Si la colección no tiene ninguno, la
 * pieza se quita en vez de quedarse con un texto de muestra.
 *
 *   · `cms.list`: el contenedor que se repite por ficha, en la página de lista.
 *   · `cms.link`: el enlace a la página de la ficha.
 *   · `cms.collectionName` / `cms.collectionIntro`: el nombre y la descripción
 *     de la colección, en la página de lista.
 */
export const CMS_SLOT_ROLES = ['title', 'image', 'description', 'content', 'date', 'categories'];
/**
 * Los huecos de un bloque de entrada y el campo del blog al que se enlazan.
 * El bloque los marca con `aiSlot` (`blog.title`…); al aplicar el diseño, el
 * editor cambia cada marca por un enlace al campo (`bindBlogSlots`).
 */
export const BLOG_SLOT_FIELDS = {
    'blog.title': 'title',
    'blog.excerpt': 'excerpt',
    'blog.cover': 'image',
    'blog.content': 'content',
    'blog.author': 'author',
    'blog.date': 'date',
    'blog.categories': 'categories',
    'blog.readingTime': 'readingTime',
    'blog.authorAvatar': 'authorAvatar',
};
/**
 * El aviso de un blog sin entradas, en la lista: ocupa la fila entera de la
 * rejilla, centrado y con la letra y el color de la sección.
 */
/* Con el color de texto de su sección: en una oscura, el claro (`--wb-auto-text-color`). */
export function blogEmptyHtml(text) {
    return `<p data-blog-empty style="grid-column:1/-1;flex:1 1 100%;margin:0;padding:56px 16px;text-align:center;font-family:var(--wb-font-body,inherit);font-size:16px;line-height:1.5;color:var(--wb-auto-text-color,var(--wb-text,inherit));opacity:.7">${escapeTemplateText(text)}</p>`;
}
//# sourceMappingURL=blog.js.map